import { Editor, Node, Extension, Mark } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import ResizeImage from './spu_resize_image.js';
import { PaginationPlus } from 'tiptap-pagination-plus';
import { tableEditing } from '@tiptap/pm/tables';
import { TablePlus, TableRowPlus, TableHeaderPlus, TableCellPlus } from 'tiptap-table-plus';

const VARIABLE_TYPE = 'spuVariable';
const INDENT_STEP = 20;
const MAX_INDENT = 420;

const TableSelectionSupport = Extension.create({
  name: 'tableSelectionSupport',

  addProseMirrorPlugins() {
    return [
      tableEditing({
        allowTableNodeSelection: false,
      }),
    ];
  },
});

const SpuVariable = Node.create({
  name: VARIABLE_TYPE,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      field_key: { default: '' },
      field_label: { default: '' },
      source: { default: 'form_field' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-spu-variable="true"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const key = HTMLAttributes.field_key || '';
    const label = HTMLAttributes.field_label || key;
    const source = HTMLAttributes.source || 'form_field';
    return [
      'span',
      {
        ...HTMLAttributes,
        'data-spu-variable': 'true',
        'data-spu-variable-source': source,
        class: `spu-template-variable-chip spu-template-variable-chip-${source}`,
        contenteditable: 'false',
      },
      `{{${label}}}`,
    ];
  },

  renderText({ node }) {
    const key = node?.attrs?.field_key || '';
    return key ? `{{${key}}}` : '';
  },
});

const Underline = Mark.create({
  name: 'underline',

  parseHTML() {
    return [
      { tag: 'u' },
      {
        style: 'text-decoration',
        getAttrs: (value) => (String(value || '').includes('underline') ? {} : false),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['u', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      setUnderline:
        () =>
        ({ chain }) =>
          chain().setMark(this.name).run(),
      toggleUnderline:
        () =>
        ({ chain }) =>
          chain().toggleMark(this.name).run(),
      unsetUnderline:
        () =>
        ({ chain }) =>
          chain().unsetMark(this.name).run(),
    };
  },
});

export function createSpuTemplateEditor({ element, content, placeholder, onUpdate, onSelectionUpdate, onFocus, onHeaderClick, onFooterClick, pageMetrics = {}, enablePagination = false, editable = true, editorClassName = '', includeFormControlClass = true, maxContentHeightPx = null }) {
  const PAGE_HEIGHT_PX = parseInt(pageMetrics.pageHeightPx || 1123, 10) || 1123;
  const PAGE_WIDTH_PX = parseInt(pageMetrics.pageWidthPx || 794, 10) || 794;
  const PAGE_GAP_PX = parseInt(pageMetrics.pageGapPx || 32, 10) || 32;
  const MARGIN_TOP = parseInt(pageMetrics.marginTopPx || 68, 10) || 68;
  const MARGIN_BOTTOM = parseInt(pageMetrics.marginBottomPx || 68, 10) || 68;
  const MARGIN_LEFT = parseInt(pageMetrics.marginLeftPx || 45, 10) || 45;
  const MARGIN_RIGHT = parseInt(pageMetrics.marginRightPx || 68, 10) || 68;
  const CONTENT_MARGIN_TOP = parseInt(pageMetrics.contentMarginTopPx || 0, 10) || 0;
  const CONTENT_MARGIN_BOTTOM = parseInt(pageMetrics.contentMarginBottomPx || 0, 10) || 0;
  const HEADER_LEFT = String(pageMetrics.headerLeft || '');
  const HEADER_RIGHT = String(pageMetrics.headerRight || '');
  const FOOTER_LEFT = String(pageMetrics.footerLeft || '');
  const FOOTER_RIGHT = String(pageMetrics.footerRight || '');

  const FontSize = Extension.create({
    name: 'fontSize',
    addOptions() {
      return {
        types: ['textStyle'],
      };
    },
    addGlobalAttributes() {
      return [
        {
          types: this.options.types,
          attributes: {
            fontSize: {
              default: null,
              parseHTML: (element) => element.style.fontSize || null,
              renderHTML: (attributes) => {
                if (!attributes.fontSize) return {};
                return { style: `font-size: ${attributes.fontSize}` };
              },
            },
          },
        },
      ];
    },
    addCommands() {
      return {
        setFontSize:
          (fontSize) =>
          ({ chain }) =>
            chain().setMark('textStyle', { fontSize }).run(),
        unsetFontSize:
          () =>
          ({ chain }) =>
            chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
      };
    },
  });

  const Indent = Extension.create({
    name: 'indent',
    addGlobalAttributes() {
      return [
        {
          types: ['paragraph', 'heading'],
          attributes: {
            indent: {
              default: 0,
              parseHTML: (element) => {
                const margin = parseInt(element.style.marginLeft || '0', 10);
                return Number.isNaN(margin) ? 0 : margin;
              },
              renderHTML: (attributes) => {
                if (!attributes.indent || attributes.indent <= 0) return {};
                return { style: `margin-left: ${attributes.indent}px` };
              },
            },
            textIndent: {
              default: 0,
              parseHTML: (element) => {
                const indent = parseInt(element.style.textIndent || '0', 10);
                return Number.isNaN(indent) ? 0 : indent;
              },
              renderHTML: (attributes) => {
                if (!attributes.textIndent || attributes.textIndent === 0) return {};
                return { style: `text-indent: ${attributes.textIndent}px` };
              },
            },
          },
        },
      ];
    },
    addCommands() {
      return {
        indent:
          () =>
          ({ tr, state, dispatch }) => {
            const { selection, doc } = state;
            const pos = selection.$from.before();
            const node = doc.nodeAt(pos);
            if (!node) return false;
            const current = node.attrs.indent || 0;
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: current + INDENT_STEP });
            return true;
          },
        outdent:
          () =>
          ({ tr, state, dispatch }) => {
            const { selection, doc } = state;
            const pos = selection.$from.before();
            const node = doc.nodeAt(pos);
            if (!node) return false;
            const current = node.attrs.indent || 0;
            const nextIndent = Math.max(0, current - INDENT_STEP);
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: nextIndent });
            return true;
          },
        setIndentPx:
          (indentPx) =>
          ({ tr, state, dispatch }) => {
            const { selection, doc } = state;
            const pos = selection.$from.before();
            const node = doc.nodeAt(pos);
            if (!node) return false;
            const nextIndent = Math.max(0, Math.min(MAX_INDENT, parseInt(indentPx, 10) || 0));
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: nextIndent });
            return true;
          },
        setTextIndentPx:
          (textIndentPx) =>
          ({ tr, state, dispatch }) => {
            const { selection, doc } = state;
            const pos = selection.$from.before();
            const node = doc.nodeAt(pos);
            if (!node) return false;
            const nextTextIndent = Math.max(0, Math.min(MAX_INDENT, parseInt(textIndentPx, 10) || 0));
            if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, textIndent: nextTextIndent });
            return true;
          },
      };
    },
  });

  const safeContent =
    content && typeof content === 'object' && content.type === 'doc'
      ? content
      : { type: 'doc', content: [{ type: 'paragraph' }] };
  const normalizedEditorClassName = String(editorClassName || '').trim();
  const baseEditorClass = [includeFormControlClass ? 'form-control' : '', 'fp-template-editor', normalizedEditorClassName].filter(Boolean).join(' ');

  const paginationExtension = enablePagination
    ? [PaginationPlus.configure({
        pageHeight: PAGE_HEIGHT_PX,
        pageWidth: PAGE_WIDTH_PX,
        pageGap: PAGE_GAP_PX,
        pageBreakBackground: '#ffffff',
        pageGapBorderColor: '#d8dce2',
        pageGapBorderSize: 1,
        marginTop: MARGIN_TOP,
        marginBottom: MARGIN_BOTTOM,
        marginLeft: MARGIN_LEFT,
        marginRight: MARGIN_RIGHT,
        contentMarginTop: CONTENT_MARGIN_TOP,
        contentMarginBottom: CONTENT_MARGIN_BOTTOM,
        footerLeft: FOOTER_LEFT,
        footerRight: FOOTER_RIGHT,
        headerLeft: HEADER_LEFT,
        headerRight: HEADER_RIGHT,
        onHeaderClick,
        onFooterClick,
      })]
    : [];

  const clearPaginationInlineStyles = (editor) => {
    const dom = editor?.view?.dom;
    if (!dom || enablePagination) return;

    dom.classList.remove('rm-with-pagination');
    [
      '--rm-max-content-child-height',
      '--rm-page-content-first',
      '--rm-page-content-1',
      '--rm-page-height',
      '--rm-margin-top',
      '--rm-margin-bottom',
      '--rm-margin-left',
      '--rm-margin-right',
      '--rm-content-margin-top',
      '--rm-content-margin-bottom',
      '--rm-page-gap-border-color',
      '--rm-page-width',
    ].forEach((cssVar) => dom.style.removeProperty(cssVar));

    dom.style.removeProperty('border');
    dom.style.removeProperty('padding-left');
    dom.style.removeProperty('padding-right');
    dom.style.removeProperty('width');
    };

  const ensureTrailingParagraphAfterTable = (editor) => {
    const doc = editor?.state?.doc;
    const lastNode = doc?.lastChild;
    if (!lastNode || lastNode.type?.name !== 'table') return;

    const endPos = doc.content.size;
    editor.commands.insertContentAt(endPos, { type: 'paragraph' });
  };

  const estimateLineHeightPx = (dom) => {
    if (!dom || !window?.getComputedStyle) return 18;
    const styles = window.getComputedStyle(dom);
    const parsed = parseFloat(styles.lineHeight);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    const fontSize = parseFloat(styles.fontSize) || 16;
    return Math.max(18, Math.round(fontSize * 1.35));
  };

  const shouldBlockNewLine = (editor) => {
    const limitPx = parseInt(maxContentHeightPx || 0, 10) || 0;
    if (limitPx <= 0) return false;

    const dom = editor?.view?.dom;
    if (!dom) return false;

    const currentHeight = Math.max(
      parseInt(dom.scrollHeight || 0, 10) || 0,
      parseInt(dom.getBoundingClientRect?.().height || 0, 10) || 0,
    );
    const nextHeight = currentHeight + estimateLineHeightPx(dom);
    return nextHeight > limitPx;
  };

  let lastPointerDown = null;

  const hasNonCollapsedDomSelection = (view) => {
    const root = view?.dom;
    const selection = window?.getSelection?.();
    if (!root || !selection || selection.rangeCount === 0 || selection.isCollapsed) return false;

    return root.contains(selection.anchorNode) || root.contains(selection.focusNode);
  };

  const pointerMovedSinceMouseDown = (event) => {
    if (!lastPointerDown || event?.clientX == null || event?.clientY == null) return false;

    return Math.abs(event.clientX - lastPointerDown.x) > 3 || Math.abs(event.clientY - lastPointerDown.y) > 3;
  };

  const editor = new Editor({
    element,
    content: safeContent,
    editable,
    extensions: [
      StarterKit,
      TextStyle,
      FontFamily,
      Underline,
      FontSize,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ResizeImage.configure({ inline: false }),
      TablePlus.configure({ resizable: true }),
      TableRowPlus,
      TableHeaderPlus,
      TableCellPlus,
      TableSelectionSupport,
      ...paginationExtension,
      Indent,
      Placeholder.configure({ placeholder: placeholder || 'Escreva o template...' }),
      SpuVariable,
    ],
    editorProps: {
      attributes: {
        class: baseEditorClass,
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          if (event?.button === 0) {
            lastPointerDown = { x: event.clientX, y: event.clientY };
          }

          const target = event?.target;
          if (!target || !target.closest) return false;

          // Ignore interaction with pagination decoration layers to avoid
          // unstable page-count recalculation loops.
          const blocked = target.closest('.rm-pagination-gap, .rm-page-header, .rm-page-footer, .breaker');
          if (!blocked) return false;

          event.preventDefault();
          if (editable) view.focus();
          return true;
        },
        click: (view, event) => {
          if (!editable) return false;
          if (!view.state.selection.empty || hasNonCollapsedDomSelection(view) || pointerMovedSinceMouseDown(event)) return false;

          const target = event?.target;
          if (!target || !target.closest) return false;

          const clickedInsideEditor =
            target.closest('.fp-template-editor, .rm-page-break, .page') &&
            !target.closest('p, h1, h2, h3, h4, h5, h6, li, ul, ol, blockquote, pre, code, table, tr, td, th, img, span[data-spu-variable="true"]');

          if (!clickedInsideEditor) return false;

          editor.chain().focus('end').run();
          return true;
        },
        focusin: () => {
          if (typeof onFocus === 'function') onFocus(editor);
          return false;
        },
      },
      handleKeyDown: (view, event) => {
        if (event.key === 'Enter' || event.key === 'NumpadEnter') {
          if (shouldBlockNewLine(editor)) {
            event.preventDefault();
            return true;
          }
        }

        if (event.key !== 'Backspace') return false;
        const { state, dispatch } = view;
        const { selection } = state;
        if (!selection.empty) return false;

        const $from = selection.$from;
        const before = $from.nodeBefore;
        if (!before || before.type.name !== 'table') return false;

        const from = $from.pos - before.nodeSize;
        const to = $from.pos;
        dispatch(state.tr.delete(from, to));
        return true;
      },
    },
    onCreate: ({ editor }) => {
      clearPaginationInlineStyles(editor);
      ensureTrailingParagraphAfterTable(editor);
    },
    onUpdate: ({ editor }) => {
      clearPaginationInlineStyles(editor);
      ensureTrailingParagraphAfterTable(editor);
      if (typeof onUpdate === 'function') onUpdate(editor.getJSON());
    },
    onSelectionUpdate: ({ editor }) => {
      if (typeof onSelectionUpdate === 'function') onSelectionUpdate(editor);
    },
  });

  return editor;
}

export function insertSpuVariable(editor, variable) {
  if (!editor || !variable || !variable.key) return;

  editor
    .chain()
    .focus()
    .insertContent({
      type: VARIABLE_TYPE,
      attrs: {
        field_key: variable.key,
        field_label: variable.label || variable.key,
        source: variable.source || 'form_field',
      },
    })
    .insertContent({ type: 'text', text: ' ' })
    .run();
}

export function extractVariableKeysFromTemplateJson(templateJson) {
  const keys = new Set();

  const walk = (node) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node !== 'object') return;

    if (node.type === VARIABLE_TYPE && node.attrs && typeof node.attrs === 'object') {
      const key = String(node.attrs.field_key || '').trim();
      if (key) keys.add(key);
    }

    if (node.content) walk(node.content);
    if (node.marks) walk(node.marks);
  };

  walk(templateJson);
  walk(templateJson?.layout?.header?.content_json);
  walk(templateJson?.layout?.footer?.content_json);
  return Array.from(keys);
}
